import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { cloneDeep, isEqual, last } from 'lodash';

import { useMetaStore } from '@apps/main/new-desktop/core/stores';

import type {
  AccommodationAvailabilityUserCriteria,
  ActivityAvailabilityUserCriteria,
  ActivitySuggestionsSearchCriteria,
  BusinessCriteria,
  DestinationAvailabilitySearchUserCriteria,
  ItinerarySearchUserCriteria,
  SearchSuggestionCriteria,
  TransportAvailabilityUserCriteria,
} from '@libs/trip-planner-interfaces';
import {
  AutoCompleteSearchType,
  IngredientType,
  ProductSearchType,
  SelectedHotelTypeQL,
} from '@libs/trip-planner-interfaces';
import { SemboDate } from '@libs/sembo-dates';

import type { ItinerariesRecipe } from '../criteria/itinerarySearchCriteria/itinerariesCriteria';
import { adaptRecipeToItinerarySearchCriteria } from '../criteria/itinerarySearchCriteria/itinerariesCriteria';
import { usePreferredTimeStore } from '../store/preferredTimeStore';
import { useItineraryStore } from '../store/itineraryStore';
import type { DestinationAvailabilityRecipe } from '../criteria/destinationAvailabilitySearchCriteria/destinationAvialabilityCriteria';
import { adaptRecipeToDestinationAvailabilitySearchCriteria } from '../criteria/destinationAvailabilitySearchCriteria/destinationAvialabilityCriteria';
import { useEditingIngredientStore } from '../store/editingIngredientStore';
import {
  getEndSearchDateFromPreferredDay,
  getStartSearchDateFromPreferredDay,
} from '../store/preferredTimeStore/preferredTimeStoreLogic';
import type {
  StayIngredient,
  RecipeMainIngredient,
} from '../recipe/ingredients/typeGuards';
import {
  isHotelIngredient,
  isHotelIngredientAdapter,
  isOwnStayIngredient,
  isOwnStayIngredientAdapter,
} from '../recipe/ingredients/typeGuards';

import type { RecipeState } from './recipeState';
import type { DateWithItinerary, TripState } from './tripState';
import type { ProductDetailState } from './productDetailState';

export interface DatesToSearch {
  startDate?: Date;
  endDate?: Date;
  selectedDate?: Date;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export function setupCriteriaState(
  recipeState: RecipeState,
  tripState: TripState,
  productDetailState: ProductDetailState
) {
  const {
    pax,
    recipe,
    searchActivities,
    metaData,
    searchForPackageRates,
    getHotelIngredient,
    getStayIngredient,
  } = recipeState;
  const preferredTimeStore = usePreferredTimeStore();
  const { preferredDay, startSearchDate, endSearchDate } =
    storeToRefs(preferredTimeStore);
  const itineraryStore = useItineraryStore();
  const editingIngredientStore = useEditingIngredientStore();
  const metaStore = useMetaStore();

  const {
    destinationProductDetailCode,
    destinationProductDetailIngredientId,
    productSearchType,
  } = productDetailState;

  const itineraryCriteriaDependencies = computed<ItinerariesRecipe>(() => ({
    ingredients: recipe.value,
    startDate: startSearchDate.value,
    endDate: endSearchDate.value,
    pax: pax.value.rooms,
  }));

  const itineraryCriteria = ref<ItinerarySearchUserCriteria>(
    adaptRecipeToItinerarySearchCriteria(
      {
        ingredients: itineraryCriteriaDependencies.value.ingredients,
        startDate: itineraryCriteriaDependencies.value.startDate,
        endDate: itineraryCriteriaDependencies.value.endDate,
        pax: itineraryCriteriaDependencies.value.pax,
      },
      tripState.sessionId.value
    )
  );

  watch(
    itineraryCriteriaDependencies,
    (dependencies) => {
      const { ingredients, pax, endDate, startDate } = dependencies;

      const finalStartDate = preferredDay.value
        ? getStartSearchDateFromPreferredDay(preferredDay.value)
        : startDate;
      const finalEndDate = preferredDay.value
        ? getEndSearchDateFromPreferredDay(preferredDay.value)
        : endDate;

      const newItineraryCriteria = adaptRecipeToItinerarySearchCriteria(
        {
          ingredients,
          pax,
          endDate: finalEndDate,
          startDate: finalStartDate,
        },
        tripState.sessionId.value
      );

      const isSameCriteria = isEqual(
        itineraryCriteria.value,
        newItineraryCriteria
      );

      if (isSameCriteria) {
        return;
      }

      itineraryCriteria.value = newItineraryCriteria;
    },
    { deep: true }
  );

  const destinationAvailabilitySearchUserCriteria = computed<
    DestinationAvailabilitySearchUserCriteria | undefined
  >(() => {
    const destinationIngredient = editingIngredientStore.editingStayIngredient;

    if (!destinationIngredient || destinationIngredient.isNewIngredient) {
      return;
    }

    let datesToSearch: DatesToSearch = {};

    try {
      datesToSearch = getDatesToSearch(
        destinationIngredient,
        searchActivities.value,
        preferredDay.value
      );
    } catch {
      return;
    }

    const destinationAvailabilityRecipe: DestinationAvailabilityRecipe = {
      destinationIngredient,
      itineraryCriteria: itineraryCriteria.value,
      searchActivities: searchActivities.value,
      datesToSearch,
    };

    return adaptRecipeToDestinationAvailabilitySearchCriteria(
      destinationAvailabilityRecipe
    );
  });

  const activitySuggestionsCriteria = computed<
    ActivitySuggestionsSearchCriteria[]
  >(() =>
    recipeState.recipe.value.reduce(
      (
        acc: ActivitySuggestionsSearchCriteria[],
        ingredient: RecipeMainIngredient,
        index: number
      ) => {
        if (isHotelIngredient(ingredient) || isOwnStayIngredient(ingredient)) {
          const criteria = cloneDeep(itineraryCriteria.value);
          const ingredientFromCriteria = criteria.ingredients
            ? criteria.ingredients[index]
            : null;

          if (
            ingredientFromCriteria &&
            (isHotelIngredientAdapter(ingredientFromCriteria) ||
              isOwnStayIngredientAdapter(ingredientFromCriteria))
          ) {
            ingredientFromCriteria.constraint.selectedHotelTypes = [
              SelectedHotelTypeQL.Golf,
              SelectedHotelTypeQL.Ticket,
            ];
          }
          acc.push({
            userCriteria: {
              recipe: criteria,
              ingredientIndex: index,
              day: itineraryStore.tripStartDate,
              constraint: {
                destination: ingredient.searchText,
              },
            },
            businessCriteria: businessCriteria.value,
          });
        }
        return acc;
      },
      []
    )
  );

  const transportAvailabilityCriteria = computed<
    TransportAvailabilityUserCriteria | undefined
  >(() => {
    if (
      !editingIngredientStore.editingTransportIngredient ||
      !editingIngredientStore.editingTransportIngredient.isIngredientCorrect()
    ) {
      return;
    }

    if (!itineraryStore.selectedItinerary?.date) {
      return;
    }

    return {
      day: itineraryStore.selectedItinerary.date,
      recipe: itineraryCriteria.value,
      ingredientIndex: recipe.value.indexOf(
        editingIngredientStore.editingTransportIngredient
      ),
      elementsInPage: 200,
      page: 0,
    };
  });

  const searchActivitiesInDestinationCriteria = computed(() =>
    recipeState.stayOnlyRecipe.value.map((stayIngredient) => ({
      ingredientId: stayIngredient.ingredientId,
      searchText: stayIngredient.searchText,
      nearbyPolygonText: isOwnStayIngredient(stayIngredient)
        ? undefined
        : stayIngredient.nearbyPolygonText,
    }))
  );

  const accommodationProductAvailabilityCriteria = computed<
    AccommodationAvailabilityUserCriteria | undefined
  >(() => {
    const productCode = destinationProductDetailCode.value;
    const ingredientId = destinationProductDetailIngredientId.value;
    const isHotelProduct =
      productSearchType.value === ProductSearchType.Accommodation;

    if (!productCode || !ingredientId || !isHotelProduct) {
      return;
    }

    const ingredientIndex = recipe.value.findIndex(
      (ingredient) => ingredient.ingredientId === ingredientId
    );

    if (ingredientIndex === -1) {
      return;
    }

    const ingredient = getHotelIngredient(ingredientId);

    const { startDate, endDate, selectedDate } = getDatesToSearch(
      ingredient,
      false,
      preferredDay.value
    );

    const recipeWithDates: ItinerarySearchUserCriteria = {
      ...itineraryCriteria.value,
      startSearchDate: startDate,
      endSearchDate: endDate,
    };

    return {
      recipe: recipeWithDates,
      ingredientIndex,
      productCode,
      day: selectedDate,
    };
  });

  const activityProductAvailabilityCriteria = computed<
    ActivityAvailabilityUserCriteria | undefined
  >(() => {
    const productCode = destinationProductDetailCode.value;
    const ingredientId = destinationProductDetailIngredientId.value;
    const isActivityProduct =
      productSearchType.value === ProductSearchType.Activity;

    if (!productCode || !ingredientId || !isActivityProduct) {
      return;
    }

    const ingredientResponse =
      itineraryStore.getHotelIngredientResponse(ingredientId) ||
      itineraryStore.getOwnStayIngredientResponse(ingredientId);

    if (!ingredientResponse) {
      return;
    }

    const parentIngredientId = ingredientResponse.ingredientId;

    const ingredient = getStayIngredient(parentIngredientId);

    const { startDate, endDate } = getDatesToSearch(
      ingredient,
      true,
      preferredDay.value
    );

    if (!startDate || !endDate) {
      return;
    }

    return {
      productCode,
      parentIngredientId,
      startDate,
      endDate,
      pax: pax.value.rooms,
      typeOfProduct: IngredientType.Activity,
    };
  });

  // Refactor: get results directly instead of critera -> results flow
  const searchSuggestionCriteria = ref<SearchSuggestionCriteria>({
    searchText: '',
    searchType: AutoCompleteSearchType.Destination,
  });

  const businessCriteria = computed<BusinessCriteria>(() => ({
    affiliateId: metaStore.metaAffiliateId,
    metaData: metaData.value,
    searchForPackageRates: searchForPackageRates.value,
  }));

  function getDatesToSearch(
    ingredient: StayIngredient,
    isActivity: boolean,
    preferredDay?: Date
  ): DatesToSearch {
    if (!preferredDay) {
      return getDaysInMonthFromItineraryDates(itineraryStore.itineraryDates);
    }

    if (isActivity) {
      return getDaysAtDestination(ingredient);
    }

    return getDaysWithSelectedDate(ingredient);
  }

  function getDaysInMonthFromItineraryDates(
    itineraryDates: DateWithItinerary[]
  ): DatesToSearch {
    const firstMonthDate = itineraryDates[0]?.date;
    const lastMonthDate = last(itineraryDates)?.date;

    if (!firstMonthDate || !lastMonthDate) {
      return {};
    }

    return {
      startDate: firstMonthDate,
      endDate: lastMonthDate,
    };
  }

  function getDaysAtDestination(ingredient: StayIngredient): DatesToSearch {
    const ingredientResponse =
      itineraryStore.getHotelIngredientResponse(ingredient.ingredientId) ||
      itineraryStore.getOwnStayIngredientResponse(ingredient.ingredientId);

    if (!ingredientResponse) {
      throw new Error('No itinerary received for provided ingredient');
    }

    const date = ingredientResponse.date;
    const numberOfNights = ingredientResponse.numberOfNights;

    return {
      startDate: date,
      endDate: new SemboDate(date).add({ days: numberOfNights }).get(),
    };
  }

  function getDaysWithSelectedDate(ingredient: StayIngredient): DatesToSearch {
    let ingredientDate = itineraryStore.getIngredientDate(
      ingredient.ingredientId
    );

    if (!ingredientDate && preferredDay.value) {
      ingredientDate = recipeState.getIngredientDateInRecipe(
        ingredient.ingredientId,
        preferredDay.value
      );
    }

    if (!ingredientDate) {
      throw new Error('No date available on provided ingredient');
    }

    const monthDates = getDaysInMonthFromItineraryDates(
      itineraryStore.itineraryDates
    );

    return {
      startDate: monthDates.startDate,
      endDate: monthDates.endDate,
      selectedDate: ingredientDate,
    };
  }

  return {
    itineraryCriteria,
    destinationAvailabilitySearchUserCriteria,
    accommodationProductAvailabilityCriteria,
    activityProductAvailabilityCriteria,
    searchActivitiesInDestinationCriteria,
    transportAvailabilityCriteria,
    searchSuggestionCriteria,
    businessCriteria,
    activitySuggestionsCriteria,

    getDatesToSearch,
  };
}

export type CriteriaState = ReturnType<typeof setupCriteriaState>;
